import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
	AI_AGENT_AVATAR_OPTIONS,
	AgentAvatar,
	DEFAULT_AI_AGENT_AVATAR,
	getAgentAvatarOption,
} from "./avatarOptions";

function IconChevronDown({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function IconSpark({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="m12 2 2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2Z" />
		</svg>
	);
}

function IconArrowLeft({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M19 12H5" />
			<path d="m12 19-7-7 7-7" />
		</svg>
	);
}

function FieldGroup({ label, hint, children }) {
	return (
		<div className="space-y-2">
			<div className="space-y-1">
				<label className="block text-sm font-medium text-default-700 dark:text-default-200">
					{label}
				</label>
				{hint ? (
					<p className="m-0 text-xs leading-5 text-default-500 dark:text-default-400">
						{hint}
					</p>
				) : null}
			</div>
			{children}
		</div>
	);
}

function SectionCard({ title, subtitle, children }) {
	return (
		<div className="rounded-2xl border border-default-200 bg-white p-5 dark:border-default-700 dark:bg-default-950">
			<div className="mb-4">
				<h6 className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
					{title}
				</h6>
				<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
					{subtitle}
				</p>
			</div>
			{children}
		</div>
	);
}

function DropdownTrigger({ open, placeholder, children }) {
	return (
		<>
			<div className="flex min-h-[42px] w-full items-center justify-between gap-3 rounded-xl border border-default-200 bg-white px-3 py-2 text-sm text-default-700 transition-colors dark:border-default-700 dark:bg-default-950 dark:text-default-200">
				<div className="min-w-0 flex-1">
					{children || (
						<span className="text-default-400 dark:text-default-500">{placeholder}</span>
					)}
				</div>
				<IconChevronDown
					className={`size-4 shrink-0 text-default-400 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</div>
		</>
	);
}

function QueueBadge({ queue, onRemove }) {
	const queueColor = queue.color || "#6366f1";

	return (
		<span
			className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-100 px-2.5 py-1 text-xs font-medium text-default-700 dark:border-default-700 dark:bg-default-800 dark:text-default-200"
			style={{
				backgroundColor: `${queueColor}18`,
				boxShadow: `inset 0 0 0 1px ${queueColor}33`,
			}}
		>
			<span
				className="size-2 rounded-full"
				style={{ backgroundColor: queueColor }}
			/>
			{queue.name}
			{onRemove ? (
				<button
					type="button"
					onClick={onRemove}
					className="rounded-full border-0 bg-transparent p-0 text-[11px] leading-none text-default-500 hover:text-danger dark:text-default-400"
				>
					x
				</button>
			) : null}
		</span>
	);
}

export function NovoAgente() {
	const [formData, setFormData] = useState({
		nome: "",
		avatar: DEFAULT_AI_AGENT_AVATAR,
		setorAtribuido: "",
		MensagemEncerramento: "",
		objetivo: "",
		personalidade: "",
		linguagem: "formal",
		responderGrupos: false,
		permitirAgendamentos: false,
		responderTexto: true,
		responderAudio: true,
		responderVideo: true,
		responderImagem: true,
		responderSticker: true,
	});
	const [title, setTitle] = useState("");
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [queues, setQueues] = useState([]);
	const [setorDropdownOpen, setSetorDropdownOpen] = useState(false);
	const [transferDropdownOpen, setTransferDropdownOpen] = useState(false);
	const [agentCount, setAgentCount] = useState(null);
	const setorDropdownRef = useRef(null);
	const transferDropdownRef = useRef(null);

	const searchParams = new URLSearchParams(window.location.search);
	const editId = searchParams.get("edit");
	const MAX_AGENTES_IA = 4;
	const atAgentLimit = !editId && agentCount != null && agentCount >= MAX_AGENTES_IA;

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/queue");
				setQueues(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		if (editId) return undefined;
		(async () => {
			try {
				const { data } = await api.post("/listarAgentes", { page: 1 });
				const list = Array.isArray(data) ? data : [];
				// listarAgentes pagina de 10; para o limite 4 basta a 1ª página
				setAgentCount(list.length);
			} catch (err) {
				toastError(err);
			}
		})();
		return undefined;
	}, [editId]);

	useEffect(() => {
		async function fetchData() {
			if (!editId) {
				setTitle("Novo Agente IA");
				return;
			}

			try {
				const { data } = await api.post(`/listarAgente/${editId}`);
				const setorAtribuido =
					data.setorAtribuido != null && data.setorAtribuido !== ""
						? Number(data.setorAtribuido)
						: "";

				let setoresTransferencia = [];
				if (Array.isArray(data.setoresTransferencia)) {
					setoresTransferencia = data.setoresTransferencia.map(Number);
				} else if (typeof data.setoresTransferencia === "string") {
					try {
						const parsed = JSON.parse(data.setoresTransferencia);
						setoresTransferencia = Array.isArray(parsed) ? parsed.map(Number) : [];
					} catch (_) {}
				}

				setFormData({
					nome: data.nome || "",
					avatar: data.avatar || DEFAULT_AI_AGENT_AVATAR,
					setorAtribuido,
					MensagemEncerramento: data.MensagemEncerramento || "",
					objetivo: data.objetivo || "",
					personalidade: data.personalidade || "",
					linguagem: data.linguagem || "formal",
					responderGrupos: Boolean(data.responderGrupos),
					permitirAgendamentos: Boolean(data.permitirAgendamentos),
					responderTexto: data.responderTexto !== false,
					responderAudio: data.responderAudio !== false,
					responderVideo: data.responderVideo !== false,
					responderImagem: data.responderImagem !== false,
					responderSticker: data.responderSticker !== false,
				});
				setSelectedQueueIds(setoresTransferencia);
				setTitle("Editar Agente IA");
			} catch (err) {
				console.error(err);
			}
		}

		fetchData();
	}, [editId]);

	useEffect(() => {
		if (!setorDropdownOpen) return undefined;

		const handleClickOutside = (event) => {
			if (setorDropdownRef.current && !setorDropdownRef.current.contains(event.target)) {
				setSetorDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [setorDropdownOpen]);

	useEffect(() => {
		if (!transferDropdownOpen) return undefined;

		const handleClickOutside = (event) => {
			if (
				transferDropdownRef.current &&
				!transferDropdownRef.current.contains(event.target)
			) {
				setTransferDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [transferDropdownOpen]);

	const selectedQueue = useMemo(
		() => queues.find((queue) => Number(queue.id) === Number(formData.setorAtribuido)),
		[queues, formData.setorAtribuido]
	);
	const selectedAvatar = useMemo(
		() => getAgentAvatarOption(formData.avatar),
		[formData.avatar]
	);

	const selectedTransferQueues = useMemo(
		() =>
			(Array.isArray(selectedQueueIds) ? selectedQueueIds : [])
				.map((id) => queues.find((queue) => Number(queue.id) === Number(id)))
				.filter(Boolean),
		[queues, selectedQueueIds]
	);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleQueueTransferToggle = (queueId) => {
		const normalizedQueueId = Number(queueId);

		setSelectedQueueIds((prev) =>
			prev.some((id) => Number(id) === normalizedQueueId)
				? prev.filter((id) => Number(id) !== normalizedQueueId)
				: [...prev, normalizedQueueId]
		);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (atAgentLimit) {
			toast.error(
				`Limite de ${MAX_AGENTES_IA} agentes de IA atingido. Remova um agente existente para criar outro.`
			);
			return;
		}

		if (!queues.length) {
			toast.error(
				"Não foi possível carregar os setores. Verifique as permissões ou faça login novamente; é necessário ter ao menos uma fila cadastrada."
			);
			return;
		}

		const setorNum = Number(formData.setorAtribuido);
		if (!Number.isFinite(setorNum) || setorNum < 1) {
			toast.error("Selecione um setor na lista antes de salvar.");
			return;
		}

		const nome = String(formData.nome ?? "").trim();
		const MensagemEncerramento = String(formData.MensagemEncerramento ?? "").trim();
		const objetivo = String(formData.objetivo ?? "").trim();
		const personalidade = String(formData.personalidade ?? "").trim();
		const linguagem = String(formData.linguagem ?? "").trim().toLowerCase();
		const avatar = String(formData.avatar ?? "").trim() || DEFAULT_AI_AGENT_AVATAR;
		const setoresTransferencia = (Array.isArray(selectedQueueIds) ? selectedQueueIds : []).map(
			(id) => Number(id)
		);

		if (!nome || !objetivo || !personalidade || !linguagem) {
			toast.error("Preencha nome, objetivo, personalidade e linguagem.");
			return;
		}

		if (linguagem !== "formal" && linguagem !== "informal") {
			toast.error("Linguagem deve ser formal ou informal.");
			return;
		}

		const payload = {
			nome,
			avatar,
			setorAtribuido: setorNum,
			MensagemEncerramento,
			objetivo,
			personalidade,
			linguagem,
			setoresTransferencia,
			responderGrupos: Boolean(formData.responderGrupos),
			permitirAgendamentos: Boolean(formData.permitirAgendamentos),
			responderTexto: formData.responderTexto !== false,
			responderAudio: formData.responderAudio !== false,
			responderVideo: formData.responderVideo !== false,
			responderImagem: formData.responderImagem !== false,
			responderSticker: formData.responderSticker !== false,
		};

		try {
			if (editId) {
				await api.put(`/editarAgente/${editId}`, payload);
				toast.success("Agente IA atualizado com sucesso!");
			} else {
				await api.post("/criarIa", payload);
				toast.success("Agente IA criado com sucesso!");
				setFormData({
					nome: "",
					avatar: DEFAULT_AI_AGENT_AVATAR,
					setorAtribuido: "",
					MensagemEncerramento: "",
					objetivo: "",
					personalidade: "",
					linguagem: "formal",
					responderGrupos: false,
					permitirAgendamentos: false,
					responderTexto: true,
					responderAudio: true,
					responderVideo: true,
					responderImagem: true,
					responderSticker: true,
				});
				setSelectedQueueIds([]);
			}

			window.location.href = "/AgenteIa";
		} catch (error) {
			console.error("Erro ao salvar IA:", error);
			const missing = error.response?.data?.missing;
			const message = error.response?.data?.message || "Erro ao salvar agente IA";
			toast.error(
				Array.isArray(missing) && missing.length
					? `${message} (${missing.join(", ")})`
					: message
			);
		}
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle title={title || "Novo Agente IA"} subtitle="Agentes IA" />

				<div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<p className="m-0 text-sm text-default-500 dark:text-default-400">
							Configure o comportamento, o setor responsável e o tom de comunicação do agente.
						</p>
					</div>
					<Link
						to="/AgenteIa"
						className="btn btn-sm w-full shrink-0 border border-default-200 bg-transparent text-default-600 no-underline hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300 lg:w-auto"
					>
						<IconArrowLeft className="size-4" />
						Voltar para lista
					</Link>
				</div>

				{!editId ? (
					<div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
						Limite da empresa: no máximo <strong>{MAX_AGENTES_IA}</strong> agentes de IA
						{agentCount != null ? ` (${agentCount}/${MAX_AGENTES_IA} em uso)` : ""}.
						{atAgentLimit
							? " Você já atingiu o limite — remova um agente na lista para criar outro."
							: ""}
					</div>
				) : null}

				<div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
					<div className="card">
						<div className="card-body space-y-5">
							<div className="rounded-2xl border border-primary/10 bg-primary/5 p-4 dark:border-primary/20 dark:bg-primary/10">
								<div className="flex items-start gap-3">
									<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
										<IconSpark className="size-4" />
									</span>
									<div>
										<h6 className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
											{editId ? "Edição do agente" : "Criação de novo agente"}
										</h6>
										<p className="mt-1 mb-0 text-sm leading-6 text-default-600 dark:text-default-300">
											Preencha os dados principais para definir como a IA responde,
											para onde transfere conversas e em qual setor atua.
										</p>
									</div>
								</div>
							</div>

							<form onSubmit={handleSubmit} className="space-y-5">
								<SectionCard
									title="Avatar do agente"
									subtitle="Escolha uma opção pronta para representar o agente na listagem e no cadastro."
								>
									<div className="mb-4 flex items-center gap-4 rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
										<AgentAvatar avatarId={formData.avatar} className="size-16 rounded-full" iconClassName="size-8" />
										<div>
											<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
												{selectedAvatar.label}
											</p>
											<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
												{selectedAvatar.description}
											</p>
										</div>
									</div>

									<div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
										{AI_AGENT_AVATAR_OPTIONS.map((option) => {
											const isActive = option.id === formData.avatar;

											return (
												<button
													key={option.id}
													type="button"
													onClick={() =>
														setFormData((prev) => ({ ...prev, avatar: option.id }))
													}
													className={`flex flex-col items-center gap-3 rounded-2xl border p-4 text-center transition-all ${
														isActive
															? "border-primary bg-primary/5 shadow-sm dark:border-primary/40 dark:bg-primary/10"
															: "border-default-200 bg-white hover:border-primary/20 hover:bg-primary/5 dark:border-default-700 dark:bg-default-950 dark:hover:bg-primary/10"
													}`}
												>
													<AgentAvatar avatarId={option.id} className="size-14 rounded-full" iconClassName="size-7" />
													<div className="space-y-1">
														<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
															{option.label}
														</p>
														<p className="m-0 text-xs leading-5 text-default-500 dark:text-default-400">
															{option.description}
														</p>
													</div>
												</button>
											);
										})}
									</div>
								</SectionCard>

								<SectionCard
									title="Informações principais"
									subtitle="Defina a identidade e o estilo base do agente."
								>
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
										<FieldGroup
											label="Nome do agente"
											hint="Esse nome aparece na conversa e identifica o agente."
										>
											<input
												id="nome"
												name="nome"
												type="text"
												required
												value={formData.nome}
												onChange={handleChange}
												className="form-input w-full"
												placeholder="Ex: Assistente Virtual"
											/>
										</FieldGroup>

										<FieldGroup
											label="Linguagem"
											hint="Escolha se a comunicação do agente será formal ou informal."
										>
											<select
												id="linguagem"
												name="linguagem"
												required
												value={formData.linguagem}
												onChange={handleChange}
												className="form-select w-full"
											>
												<option value="formal">Formal</option>
												<option value="informal">Informal</option>
											</select>
										</FieldGroup>

										<div className="md:col-span-2">
											<FieldGroup
												label="Objetivo da IA"
												hint="Explique de forma direta o que esse agente deve resolver."
											>
												<input
													id="objetivo"
													name="objetivo"
													type="text"
													required
													value={formData.objetivo}
													onChange={handleChange}
													className="form-input w-full"
													placeholder="Ex: Suporte ao cliente"
												/>
											</FieldGroup>
										</div>

										<div className="md:col-span-2">
											<FieldGroup
												label="Personalidade"
												hint="Descreva o tom e a postura que a IA deve adotar."
											>
												<input
													id="personalidade"
													name="personalidade"
													type="text"
													required
													value={formData.personalidade}
													onChange={handleChange}
													className="form-input w-full"
													placeholder="Ex: Profissional e prestativo"
												/>
											</FieldGroup>
										</div>
									</div>
								</SectionCard>

								<SectionCard
									title="Atuação e transferência"
									subtitle="Defina onde o agente opera e para quais setores pode encaminhar."
								>
									<div className="grid grid-cols-1 gap-4">
										<FieldGroup
											label="Setor principal"
											hint="O agente responderá quando o atendimento entrar nesse setor."
										>
											<div className="relative" ref={setorDropdownRef}>
												<button
													type="button"
													onClick={() => setSetorDropdownOpen((prev) => !prev)}
													className="w-full text-left"
												>
													<DropdownTrigger
														open={setorDropdownOpen}
														placeholder="Selecione um setor"
													>
														{selectedQueue ? <QueueBadge queue={selectedQueue} /> : null}
													</DropdownTrigger>
												</button>

												{setorDropdownOpen && (
													<div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-default-200 bg-white shadow-lg dark:border-default-700 dark:bg-default-900">
														{queues.length === 0 ? (
															<p className="m-0 px-4 py-4 text-center text-sm text-default-500 dark:text-default-400">
																Nenhum setor cadastrado.
															</p>
														) : (
															queues.map((queue, index) => {
																const isSelected =
																	Number(formData.setorAtribuido) === Number(queue.id);

																return (
																	<button
																		key={queue.id}
																		type="button"
																		onClick={() => {
																			setFormData((prev) => ({
																				...prev,
																				setorAtribuido: Number(queue.id),
																			}));
																			setSetorDropdownOpen(false);
																		}}
																		className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-default-100 dark:hover:bg-default-800 ${
																			index !== queues.length - 1
																				? "border-b border-default-100 dark:border-default-800"
																				: ""
																		} ${isSelected ? "bg-primary/5 dark:bg-primary/10" : ""}`}
																	>
																		<span
																			className="size-2.5 rounded-full"
																			style={{ backgroundColor: queue.color }}
																		/>
																		<span className="flex-1 text-default-700 dark:text-default-200">
																			{queue.name}
																		</span>
																		{isSelected ? (
																			<span className="text-xs font-semibold text-primary">
																				Selecionado
																			</span>
																		) : null}
																	</button>
																);
															})
														)}
													</div>
												)}
											</div>
										</FieldGroup>

										<FieldGroup
											label="Setores para transferência"
											hint="Libere os setores possíveis. Depois, em Conhecimentos → categoria “Regras de transferência”, escreva QUANDO transferir para cada um. A IA usa a tag [TRANSFER]setor[/TRANSFER]."
										>
											<div className="relative" ref={transferDropdownRef}>
												<button
													type="button"
													onClick={() => setTransferDropdownOpen((prev) => !prev)}
													className="w-full text-left"
												>
													<DropdownTrigger
														open={transferDropdownOpen}
														placeholder="Selecione os setores"
													>
														{selectedTransferQueues.length ? (
															<div className="flex flex-wrap gap-2">
																{selectedTransferQueues.map((queue) => (
																	<QueueBadge key={queue.id} queue={queue} />
																))}
															</div>
														) : null}
													</DropdownTrigger>
												</button>

												{transferDropdownOpen && (
													<div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-default-200 bg-white shadow-lg dark:border-default-700 dark:bg-default-900">
														{queues.length === 0 ? (
															<p className="m-0 px-4 py-4 text-center text-sm text-default-500 dark:text-default-400">
																Nenhum setor cadastrado.
															</p>
														) : (
															queues.map((queue, index) => {
																const isSelected = (
																	Array.isArray(selectedQueueIds) ? selectedQueueIds : []
																).some((id) => Number(id) === Number(queue.id));

																return (
																	<button
																		key={queue.id}
																		type="button"
																		onClick={() => handleQueueTransferToggle(queue.id)}
																		className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-default-100 dark:hover:bg-default-800 ${
																			index !== queues.length - 1
																				? "border-b border-default-100 dark:border-default-800"
																				: ""
																		}`}
																	>
																		<input
																			type="checkbox"
																			readOnly
																			checked={isSelected}
																			className="size-4 rounded border-default-300 text-primary focus:ring-primary"
																		/>
																		<span
																			className="size-2.5 rounded-full"
																			style={{ backgroundColor: queue.color }}
																		/>
																		<span className="flex-1 text-default-700 dark:text-default-200">
																			{queue.name}
																		</span>
																	</button>
																);
															})
														)}
													</div>
												)}
											</div>

											{selectedTransferQueues.length ? (
												<div className="flex flex-wrap gap-2 pt-2">
													{selectedTransferQueues.map((queue) => (
														<QueueBadge
															key={queue.id}
															queue={queue}
															onRemove={() => handleQueueTransferToggle(queue.id)}
														/>
													))}
												</div>
											) : null}
										</FieldGroup>
									</div>
								</SectionCard>

								<SectionCard
									title="Tipos de mensagem"
									subtitle="Tudo começa ativo. Desmarque o que este agente não deve responder."
								>
									<div className="grid gap-3 sm:grid-cols-2">
										{[
											["responderTexto", "Texto"],
											["responderAudio", "Áudio"],
											["responderVideo", "Vídeo"],
											["responderImagem", "Imagem"],
											["responderSticker", "Sticker"],
										].map(([name, label]) => (
											<label
												key={name}
												className="flex cursor-pointer items-center gap-3 rounded-2xl border border-default-200 bg-default-50 px-4 py-3 dark:border-default-700 dark:bg-default-900"
											>
												<input
													type="checkbox"
													name={name}
													checked={formData[name] !== false}
													onChange={(e) =>
														setFormData((prev) => ({
															...prev,
															[name]: e.target.checked,
														}))
													}
													className="size-4 rounded border-default-300 text-primary focus:ring-primary"
												/>
												<span className="text-sm font-semibold text-default-800 dark:text-default-100">
													{label}
												</span>
											</label>
										))}
									</div>
								</SectionCard>

								<SectionCard
									title="Grupos"
									subtitle="Defina se este agente pode atender conversas em grupos."
								>
									<label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
										<input
											type="checkbox"
											name="responderGrupos"
											checked={Boolean(formData.responderGrupos)}
											onChange={(e) =>
												setFormData((prev) => ({
													...prev,
													responderGrupos: e.target.checked,
												}))
											}
											className="mt-1 size-4 rounded border-default-300 text-primary focus:ring-primary"
										/>
										<span>
											<span className="block text-sm font-semibold text-default-800 dark:text-default-100">
												Responder grupos
											</span>
											<span className="mt-1 block text-xs text-default-500 dark:text-default-400">
												Se ativo, a IA também responde mensagens de grupos. Se inativo, ela só atende conversas individuais.
											</span>
										</span>
									</label>
								</SectionCard>

								<SectionCard
									title="Agendamentos"
									subtitle="Controle se este agente pode marcar horários no Google Agenda."
								>
									<label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
										<input
											type="checkbox"
											name="permitirAgendamentos"
											checked={Boolean(formData.permitirAgendamentos)}
											onChange={(e) =>
												setFormData((prev) => ({
													...prev,
													permitirAgendamentos: e.target.checked,
												}))
											}
											className="mt-1 size-4 rounded border-default-300 text-primary focus:ring-primary"
										/>
										<span>
											<span className="block text-sm font-semibold text-default-800 dark:text-default-100">
												Permitir agendamentos
											</span>
											<span className="mt-1 block text-xs text-default-500 dark:text-default-400">
												Se ativo e a empresa tiver Google Calendar conectado, a IA pode consultar horários e agendar. Se inativo, ela não faz agendamentos.
											</span>
										</span>
									</label>
								</SectionCard>

								<SectionCard
									title="Mensagem final"
									subtitle="Opcionalmente, defina uma mensagem padrão de encerramento."
								>
									<FieldGroup
										label="Mensagem de encerramento"
										hint="Essa mensagem pode ser usada quando o agente finalizar a conversa."
									>
										<textarea
											id="MensagemEncerramento"
											name="MensagemEncerramento"
											rows={4}
											value={formData.MensagemEncerramento}
											onChange={handleChange}
											className="form-input min-h-[120px] w-full"
											placeholder="Ex: Obrigado pelo contato!"
										/>
									</FieldGroup>
								</SectionCard>

								<div className="flex flex-col-reverse gap-3 border-t border-default-200 pt-5 dark:border-default-700 sm:flex-row sm:justify-end">
									<button
										type="button"
										onClick={() => window.history.back()}
										className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={atAgentLimit}
										className="btn btn-sm bg-primary text-white disabled:cursor-not-allowed disabled:opacity-60"
									>
										{editId ? "Salvar alterações" : "Criar Agente IA"}
									</button>
								</div>
							</form>
						</div>
					</div>

					<div className="card h-fit">
						<div className="card-body">
							<div className="mb-4 flex items-center gap-3">
									<AgentAvatar avatarId={formData.avatar} className="size-12 rounded-2xl" iconClassName="size-6" />
								<div>
									<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
										Resumo do agente
									</p>
									<p className="m-0 text-xs text-default-500 dark:text-default-400">
										Visualize os principais dados antes de salvar.
									</p>
								</div>
							</div>

							<div className="space-y-4">
								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Avatar
									</p>
									<div className="mt-2 flex items-center gap-3">
										<AgentAvatar avatarId={formData.avatar} className="size-12 rounded-2xl" iconClassName="size-6" />
										<div>
											<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
												{selectedAvatar.label}
											</p>
											<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
												{selectedAvatar.description}
											</p>
										</div>
									</div>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Nome
									</p>
									<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
										{formData.nome || "Não definido"}
									</p>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Setor principal
									</p>
									<div className="mt-2">
										{selectedQueue ? (
											<QueueBadge queue={selectedQueue} />
										) : (
											<p className="m-0 text-sm text-default-500 dark:text-default-400">
												Não selecionado
											</p>
										)}
									</div>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Perfil de resposta
									</p>
									<div className="mt-2 flex flex-wrap gap-2">
										<span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
											{formData.linguagem === "informal" ? "Informal" : "Formal"}
										</span>
										<span className="inline-flex rounded-full bg-default-100 px-3 py-1 text-xs font-semibold text-default-600 dark:bg-default-800 dark:text-default-300">
											{formData.responderGrupos ? "Responde grupos" : "Só individual"}
										</span>
										<span className="inline-flex rounded-full bg-default-100 px-3 py-1 text-xs font-semibold text-default-600 dark:bg-default-800 dark:text-default-300">
											{formData.permitirAgendamentos
												? "Pode agendar"
												: "Sem agendamentos"}
										</span>
										<span className="inline-flex rounded-full bg-default-100 px-3 py-1 text-xs font-semibold text-default-600 dark:bg-default-800 dark:text-default-300">
											{[
												formData.responderTexto !== false ? "Texto" : null,
												formData.responderAudio !== false ? "Áudio" : null,
												formData.responderVideo !== false ? "Vídeo" : null,
												formData.responderImagem !== false ? "Imagem" : null,
												formData.responderSticker !== false ? "Sticker" : null,
											]
												.filter(Boolean)
												.join(", ") || "Nenhum tipo"}
										</span>
										{selectedTransferQueues.length ? (
											<span className="inline-flex rounded-full bg-default-100 px-3 py-1 text-xs font-semibold text-default-600 dark:bg-default-800 dark:text-default-300">
												{selectedTransferQueues.length} setor(es) de transferência
											</span>
										) : null}
									</div>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Objetivo
									</p>
									<p className="m-0 text-sm leading-6 text-default-700 dark:text-default-200">
										{formData.objetivo || "Não informado"}
									</p>
								</div>

								<div className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
									<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Personalidade
									</p>
									<p className="m-0 text-sm leading-6 text-default-700 dark:text-default-200">
										{formData.personalidade || "Não informada"}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
				</div>
			</div>
		</MainContainer>
	);
}
